<script context="module">
  import { forwardToWebToCaseHandler } from "$lib/utilities";
  import { createEventLead, getApiMode } from "$lib/utilities/contentTypes/infor";
  import { listContentPreferencesFromState } from "$lib/utilities/formFields";
  /**
   *
   * @param {import("$lib/utilities/contentTypes/form").FormProvider} provider
   */
  export function getSubmitHandler(provider) {
    const handlerMap = {
      Salesforce: forwardToWebToCaseHandler,
      Infor: createEventLead
    };
    return handlerMap[provider];
  }
</script>

<script>
  // @ts-check
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { createEventDispatcher, getContext, onMount } from "svelte";
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { page } from "$app/stores";

  import { GridContainer, FormComponent } from "@ObamaFoundation/of-design-system";

  import { buildSrc, srcSetFromWidths, sizesForBreakpoints, SRCSET_WIDTHS } from "$lib/utilities";
  import { BREAK_POINTS } from "$lib/utilities/constants";
  // eslint-disable-next-line  @ObamaFoundation/of-svelte/no-stores
  import { sanitizeFormValues } from "$lib/utilities/contentTypes/form";

  import { selectedNewsletters } from "$lib/stores";

  /** @type {string} e.g., salesforce campaign id */
  export let id;

  /** @type {import("$lib/utilities/contentTypes/form").FormProvider} provider*/
  export let provider = "Salesforce";

  /** The type of layout under the featured layout
   * @type {import("$lib/utilities/contentTypes/form").FormLayout}
   */
  export let layout = "split";

  export let theme;
  export let title;
  export let description;
  export let image;
  export let formProps = {};
  export let formFields;
  export let formFieldGroups;
  export let result = null;
  export let confirmation;
  export let redirectUrl;
  /** @type {boolean}
   * Sometimes the parent component needs to control whether or not the form should be submitted (e.g. The NewsletterList)
   **/
  export let canSubmit = true;

  /** @type { string } */
  let className = "";
  export { className as class };

  export let resizeParams = {};

  const isContainerized = !!getContext("containerized") || layout === "slim";

  // Boolean used to ignore the Contentful Image API image resizing logic
  // Introduced to remove chromatic image flakiness caused by small differences in images
  let ignoreContentfulImgResize = getContext("ignoreContentfulImgResize");

  // Image sizes (widths) for available breakpoints.
  // These can be set using any unit, but we recommend using vw (50vw, 100vw, etc.)
  // If you don't set these, the image will be full width at all breakpoints.
  let widthsAt = {};
  const sizes = sizesForBreakpoints(widthsAt);

  const Constants = {
    formSubmitErrorMessage: "Something went wrong..."
  };

  let screenWidth = 0;
  $: shouldUseMobileAsset = screenWidth < BREAK_POINTS.md && image?.mobileImageAsset;

  // TODO: extract to helper
  $: featuredImage = {
    src: buildSrc(shouldUseMobileAsset ? image?.mobileImageAsset : image?.imageAsset),
    alt: image?.altText,
    srcset:
      !ignoreContentfulImgResize && image?.imageAsset.fields.file
        ? srcSetFromWidths(
            shouldUseMobileAsset
              ? image?.mobileImageAsset.fields.file.url
              : image?.imageAsset.fields.file.url,
            SRCSET_WIDTHS,
            resizeParams
          )
        : null,
    sizes,
    theme: image?.theme
  };

  export let formSubmitErrorMessage = "";
  let submitted = false;
  let isSubmitting = false;

  let mode = "test";
  onMount(() => {
    mode = getApiMode($page?.url);
  });

  const dispatch = createEventDispatcher();

  selectedNewsletters.subscribe((selectedNewsletters) => {
    formFields.forEach((formField) => {
      if (formField.id === "contentPreferences") {
        formField.value = listContentPreferencesFromState(selectedNewsletters);
      }
    });
  });

  /**
   *
   * @param {CustomEvent} event
   */
  async function handleSubmit(event) {
    const formValues = event.detail;

    dispatch("submit");

    if (canSubmit) {
      formSubmitErrorMessage = "";
      try {
        isSubmitting = true;

        let handlerArgs = {};

        //If this gets any longer (e.g. we add another provider), I'm abstracting it out.
        switch (provider) {
          case "Infor":
            handlerArgs = {
              fields: sanitizeFormValues({
                formFields,
                formFieldGroups,
                rawFormValues: formValues
              }),
              mode
            };
            break;

          case "Salesforce":
            handlerArgs = sanitizeFormValues({
              formFields,
              formFieldGroups,
              rawFormValues: formValues
            });
            break;

          default:
            throw new Error("Provider not yet implemented!");
        }

        result = await getSubmitHandler(provider)?.(handlerArgs);

        if (result?.status === "success") {
          if (redirectUrl) {
            window.location.href = redirectUrl;
            return;
          } else {
            submitted = true;

            const formContainer = document.getElementById(id);
            if (formContainer) {
              const top = formContainer.getBoundingClientRect().top;
              window.scroll(0, top + window.scrollY);
            }
          }
        } else {
          throw new Error("Something went wrong...");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log("Error submitting form:", err);
        formSubmitErrorMessage = err.message || Constants.formSubmitErrorMessage;
      } finally {
        isSubmitting = false;
      }
    }
  }
</script>

<svelte:window bind:innerWidth={screenWidth} />

{#key screenWidth}
  {#if isContainerized}
    <div class={className}>
      <FormComponent
        {id}
        {layout}
        {title}
        {theme}
        {featuredImage}
        {description}
        {formProps}
        {formFields}
        {formFieldGroups}
        onSubmit={handleSubmit}
        {isSubmitting}
        isFormSubmitSuccess={submitted}
        {formSubmitErrorMessage}
        {confirmation}
      />
    </div>
  {:else}
    <GridContainer containerClass={className}>
      <FormComponent
        {id}
        {layout}
        {title}
        {theme}
        {featuredImage}
        {description}
        {formProps}
        {formFields}
        {formFieldGroups}
        onSubmit={handleSubmit}
        {isSubmitting}
        isFormSubmitSuccess={submitted}
        {formSubmitErrorMessage}
        {confirmation}
      />
    </GridContainer>
  {/if}
{/key}
